import { useRef, useState } from "react";
import { api, ApiError } from "../api.ts";
import { ErrorBanner } from "../ErrorBanner.tsx";
import { Modal } from "../Modal.tsx";
import type {
  MenuItem,
  ParsedMenuItem,
  ParseMenuResponse,
  PlaceCandidate,
  ResolvedPlace,
  ResolvePlaceResponse,
  Restaurant,
} from "../types.ts";

interface Props {
  communityId: string;
  initial: Restaurant | null;
  onClose: () => void;
  onSaved: () => void;
}

interface MenuItemDraft {
  key: string;
  nameEn: string;
  nameEs: string;
  price: string;
  category: string;
  note: string;
  // Set on rows that originated from the AI menu parser so the editor can
  // flag them as "review before saving". Cleared once the admin edits nothing
  // special — it's purely a visual hint, never persisted.
  aiDraft?: boolean;
  // Per-row parse caveat from the AI (price smudged, text unclear, …). Rendered
  // inline on the affected row so the admin sees it where it matters, not lumped
  // in a banner. Visual hint only — never persisted.
  warning?: string | null;
}

let nextKey = 1;
function freshKey(): string {
  nextKey += 1;
  return `m${nextKey}`;
}

function draftFromMenuItem(m: MenuItem): MenuItemDraft {
  return {
    key: freshKey(),
    nameEn: m.nameEn,
    nameEs: m.nameEs,
    price: m.price,
    category: m.category,
    note: m.note ?? "",
  };
}

// The parser returns price as a number|null; the form edits price as free text.
function draftFromParsedItem(m: ParsedMenuItem): MenuItemDraft {
  return {
    key: freshKey(),
    nameEn: m.name_en,
    nameEs: m.name_es,
    price: m.price === null ? "" : String(m.price),
    category: m.category ?? "",
    note: m.note ?? "",
    aiDraft: true,
    warning: m.warning ?? null,
  };
}

function emptyDraft(): MenuItemDraft {
  return { key: freshKey(), nameEn: "", nameEs: "", price: "", category: "", note: "" };
}

// Map a File's MIME type onto what /admin/parse-menu accepts, or null if
// unsupported. The endpoint takes jpeg/png/gif/webp images or a PDF.
function normalizeMediaType(type: string): string | null {
  if (type === "image/jpg") return "image/jpeg";
  if (type === "application/pdf") return "application/pdf";
  return /^image\/(jpeg|png|gif|webp)$/.test(type) ? type : null;
}

// Some browsers report an empty MIME type for a dropped/picked .pdf — fall back
// to the extension so a PDF is never silently rejected.
function mediaTypeForFile(file: File): string | null {
  const direct = normalizeMediaType(file.type);
  if (direct) return direct;
  if (file.type === "" && /\.pdf$/i.test(file.name)) return "application/pdf";
  return null;
}

// data: URL → bare base64 payload (strip the "data:<mime>;base64," prefix).
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("file_read_failed"));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function RestaurantForm({ communityId, initial, onClose, onSaved }: Props) {
  const isEdit = initial !== null;
  const [name, setName] = useState(initial?.name ?? "");
  const [whatsapp, setWhatsapp] = useState(initial?.whatsappNumber ?? "");
  const [hours, setHours] = useState(initial?.hours ?? "");
  const [info, setInfo] = useState(initial?.info ?? "");
  const [menuImageUrls, setMenuImageUrls] = useState<string[]>(
    initial?.menuImageUrls ??
      (initial?.menuImageUrl ? [initial.menuImageUrl] : []),
  );
  const [items, setItems] = useState<MenuItemDraft[]>(
    initial ? initial.menuItems.map(draftFromMenuItem) : [],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ---- Maps-link fetch (FEATURE 2) ----------------------------------------
  const [mapsUrl, setMapsUrl] = useState("");
  const [mapsBusy, setMapsBusy] = useState(false);
  const [candidates, setCandidates] = useState<PlaceCandidate[] | null>(null);
  const [placeNote, setPlaceNote] = useState<string | null>(null);
  // Read-only reference: the phone Google Places lists. We DON'T autofill the
  // WhatsApp field with it — the admin enters the ordering number by hand — but
  // we surface it so they can copy it if it happens to be the right number.
  const [placePhone, setPlacePhone] = useState<string | null>(null);

  // ---- Menu parse (FEATURE 1) ---------------------------------------------
  const [parseBusy, setParseBusy] = useState(false);
  // Document-level parse warnings only (per-row caveats render on their row).
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  // Tailor the loading copy — a multi-page PDF takes noticeably longer.
  const [parsePdf, setParsePdf] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Holds a freshly-parsed draft while we ask the admin whether to append it to
  // the rows they already have or replace them — never silently clobbers.
  const [pendingParse, setPendingParse] = useState<MenuItemDraft[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function updateItem(key: string, patch: Partial<MenuItemDraft>) {
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, ...patch } : it)),
    );
  }

  function removeItem(key: string) {
    setItems((prev) => prev.filter((it) => it.key !== key));
  }

  function addItem() {
    setItems((prev) => [...prev, emptyDraft()]);
  }

  // --- Menu image links ----------------------------------------------------

  function updateMenuImageUrl(index: number, value: string) {
    setMenuImageUrls((prev) =>
      prev.map((u, i) => (i === index ? value : u)),
    );
  }

  function removeMenuImageUrl(index: number) {
    setMenuImageUrls((prev) => prev.filter((_, i) => i !== index));
  }

  function addMenuImageUrl() {
    setMenuImageUrls((prev) => [...prev, ""]);
  }

  // --- Maps-link fetch -----------------------------------------------------

  function applyResolvedPlace(place: ResolvedPlace) {
    if (place.name) setName(place.name);
    if (place.formatted_address) setInfo(place.formatted_address);
    if (place.opening_hours && place.opening_hours.weekday_text.length > 0) {
      setHours(place.opening_hours.weekday_text.join(" · "));
    }
    // Deliberately do NOT autofill WhatsApp from the Places phone — the Places
    // listing number is often not the ordering number. Surface it read-only so
    // the admin can copy it by hand if it happens to be correct.
    setPlacePhone(place.formatted_phone_number ?? null);
    setPlaceNote("AI draft from Google Maps — review every field before saving.");
  }

  function chooseCandidate(c: PlaceCandidate) {
    setName(c.name);
    if (c.formatted_address) setInfo(c.formatted_address);
    setCandidates(null);
    // Candidates carry only name + address — no phone to reference.
    setPlacePhone(null);
    setPlaceNote(
      "AI draft from the selected place — Maps gave only name + address for candidates, so add hours and the WhatsApp number by hand. Review before saving.",
    );
  }

  async function fetchPlace() {
    setError(null);
    setCandidates(null);
    setPlaceNote(null);
    setPlacePhone(null);
    const url = mapsUrl.trim();
    if (!url) {
      setError("maps_url_required");
      return;
    }
    setMapsBusy(true);
    try {
      const res = await api<ResolvePlaceResponse>("/admin/resolve-place", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ maps_url: url }),
      });
      if ("resolved" in res) {
        applyResolvedPlace(res.resolved);
      } else {
        setCandidates(res.candidates);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setMapsBusy(false);
    }
  }

  // --- Menu parse ----------------------------------------------------------

  // Shared draft-population path for both parse sources (file upload and the
  // saved menu image links). If the admin already entered (or previously
  // parsed) rows, ask before touching them; otherwise drop the draft straight
  // in. Per-row caveats ride on each draft (warning); whole-menu notes go to
  // parseWarnings. Never auto-saves.
  function applyParsedResult(res: ParseMenuResponse) {
    const drafts = res.items.map(draftFromParsedItem);
    setParseWarnings(res.warnings ?? []);
    if (items.length > 0) {
      setPendingParse(drafts);
    } else {
      setItems(drafts);
    }
  }

  async function parseFile(file: File) {
    setError(null);
    setParseWarnings([]);
    const mediaType = mediaTypeForFile(file);
    if (!mediaType) {
      setError("invalid_media_type");
      return;
    }
    setParsePdf(mediaType === "application/pdf");
    setParseBusy(true);
    try {
      const imageBase64 = await fileToBase64(file);
      const res = await api<ParseMenuResponse>("/admin/parse-menu", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          image_base64: imageBase64,
          media_type: mediaType,
        }),
      });
      applyParsedResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setParseBusy(false);
    }
  }

  // Parse the menu straight from the saved image links — no re-upload. The
  // server fetches each link itself and dedupes multi-page menus across them.
  async function parseFromLinks() {
    setError(null);
    setParseWarnings([]);
    const urls = menuImageUrls.map((u) => u.trim()).filter((u) => u.length > 0);
    if (urls.length === 0) {
      setError("invalid_image_urls");
      return;
    }
    // Links are images/PDFs the server fetches; the upload box's PDF-specific
    // copy doesn't apply here.
    setParsePdf(false);
    setParseBusy(true);
    try {
      const res = await api<ParseMenuResponse>("/admin/parse-menu", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ image_urls: urls }),
      });
      applyParsedResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setParseBusy(false);
    }
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-fires onChange.
    e.target.value = "";
    if (file) void parseFile(file);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void parseFile(file);
  }

  function appendPending() {
    if (pendingParse) setItems((prev) => [...prev, ...pendingParse]);
    setPendingParse(null);
  }

  function replacePending() {
    if (pendingParse) setItems(pendingParse);
    setPendingParse(null);
  }

  const aiBusy = mapsBusy || parseBusy;
  const hasAiDraft = items.some((it) => it.aiDraft);
  const hasMenuLink = menuImageUrls.some((u) => u.trim().length > 0);

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const trimmedName = name.trim();
      if (!trimmedName) {
        setError("name_required");
        setBusy(false);
        return;
      }
      const menu_items = items
        .map((it) => ({
          name_en: it.nameEn.trim(),
          name_es: it.nameEs.trim(),
          price: it.price.trim(),
          category: it.category.trim(),
          note: it.note.trim() ? it.note.trim() : null,
        }))
        .filter(
          (it) =>
            it.name_en || it.name_es || it.price || it.category || it.note,
        );
      const menu_image_urls = menuImageUrls
        .map((u) => u.trim())
        .filter((u) => u.length > 0);
      const payload: Record<string, unknown> = {
        name: trimmedName,
        whatsapp_number: whatsapp,
        hours,
        info,
        menu_image_urls,
        menu_items,
      };
      if (isEdit) {
        await api(`/admin/restaurants/${encodeURIComponent(initial!.id)}`, {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        payload.community_id = communityId;
        await api(`/admin/restaurants`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={isEdit ? `Edit restaurant #${initial!.id}` : "Add restaurant"}
      subtitle={
        isEdit
          ? `community #${initial!.communityId}`
          : `community #${communityId}`
      }
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="luna-btn luna-btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="luna-btn luna-btn-primary"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
                ? "Save changes"
                : "Create restaurant"}
          </button>
        </>
      }
    >
      {error && <ErrorBanner code={error} />}

      {/* FEATURE 2 — fetch details from a Google Maps link --------------- */}
      <div className="ai-assist">
        <div className="ai-assist-head">
          <span className="ai-badge">AI</span>
          <span>Fetch details from a Google Maps link</span>
        </div>
        <div className="maps-fetch">
          <input
            className="luna-input"
            value={mapsUrl}
            onChange={(e) => setMapsUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!aiBusy) void fetchPlace();
              }
            }}
            placeholder="https://maps.app.goo.gl/…  or  https://www.google.com/maps/place/…"
            disabled={mapsBusy}
            aria-label="Google Maps link"
          />
          <button
            type="button"
            className="luna-btn luna-btn-outline"
            onClick={() => void fetchPlace()}
            disabled={aiBusy}
          >
            {mapsBusy ? "Fetching…" : "Fetch"}
          </button>
        </div>
        <p className="ai-hint">
          Pre-fills name, address, hours and phone for review. Nothing is saved
          until you submit the form.
        </p>

        {candidates && (
          <div className="place-candidates">
            <div className="place-candidates-head">
              That link didn’t resolve to one place — pick the right one:
            </div>
            {candidates.map((c) => (
              <button
                type="button"
                key={c.place_id}
                className="place-candidate"
                onClick={() => chooseCandidate(c)}
              >
                <span className="place-candidate-name">{c.name}</span>
                {c.formatted_address && (
                  <span className="place-candidate-addr">
                    {c.formatted_address}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {placeNote && <div className="banner info ai-draft-note">{placeNote}</div>}
      </div>

      <div className="field">
        <label htmlFor="r-name">Name</label>
        <input
          id="r-name"
          className="luna-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Cevicheria Casa Linda"
        />
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="r-whatsapp">WhatsApp number</label>
          <input
            id="r-whatsapp"
            className="luna-input"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="+593 99 000 0000"
          />
          {placePhone && (
            <p className="field-ref-note">
              Google lists: <span className="field-ref-phone">{placePhone}</span>{" "}
              — not filled in automatically. Enter the WhatsApp ordering number
              by hand (copy this only if it’s the same).
            </p>
          )}
        </div>
        <div className="field">
          <label htmlFor="r-hours">Hours</label>
          <input
            id="r-hours"
            className="luna-input"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="Mon–Sun 11–21"
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="r-info">Info / description</label>
        <textarea
          id="r-info"
          className="luna-input"
          value={info}
          onChange={(e) => setInfo(e.target.value)}
          rows={4}
          placeholder="Family-run cevicheria. House special: encebollado."
        />
      </div>
      <div className="field">
        <label>Menu image links (optional)</label>
        <div className="menu-image-links">
          {menuImageUrls.length === 0 && (
            <p className="ai-hint">
              No menu image links yet. Add one to attach a photo or PDF of the
              menu.
            </p>
          )}
          {menuImageUrls.map((url, i) => (
            <div key={i} className="menu-image-link-row">
              <input
                className="luna-input"
                value={url}
                onChange={(e) => updateMenuImageUrl(i, e.target.value)}
                placeholder="https://…/menu.jpg"
                aria-label={`Menu image link ${i + 1}`}
              />
              <button
                type="button"
                className="remove-btn"
                onClick={() => removeMenuImageUrl(i)}
                aria-label="Remove menu image link"
              >
                ✕
              </button>
            </div>
          ))}
          <div className="menu-link-actions">
            <button
              type="button"
              className="luna-btn luna-btn-outline luna-btn-sm"
              onClick={addMenuImageUrl}
            >
              + Add link
            </button>
            <button
              type="button"
              className="luna-btn luna-btn-outline luna-btn-sm menu-parse-links-btn"
              onClick={() => void parseFromLinks()}
              disabled={aiBusy || !hasMenuLink}
              title={
                hasMenuLink
                  ? undefined
                  : "Add at least one menu image link first"
              }
            >
              <span className="ai-badge">AI</span>
              {parseBusy ? (
                <>
                  <span className="spinner" aria-hidden="true" /> Parsing links…
                </>
              ) : (
                "Parse menu from links"
              )}
            </button>
          </div>
          <p className="ai-hint">
            Reads the menu straight from the links above into draft rows below —
            no re-upload needed. Vision calls take several seconds; multiple
            links longer. Nothing is saved until you submit the form.
          </p>
        </div>
      </div>

      <div className="field">
        <label>Menu items</label>

        {/* FEATURE 1 — upload a menu photo to parse into draft rows ------ */}
        <div
          className={`menu-upload${dragging ? " is-dragging" : ""}${
            parseBusy ? " is-busy" : ""
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!parseBusy) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.pdf"
            className="menu-upload-input"
            onChange={onPickFile}
            disabled={parseBusy}
          />
          {parseBusy ? (
            <span className="menu-upload-label">
              <span className="spinner" aria-hidden="true" />{" "}
              {parsePdf
                ? "Parsing menu PDF… multi-page files can take up to a minute."
                : "Parsing menu photo… this can take a few seconds."}
            </span>
          ) : (
            <span className="menu-upload-label">
              <span className="ai-badge">AI</span>
              Drop a menu photo or PDF here, or{" "}
              <button
                type="button"
                className="linklike"
                onClick={() => fileInputRef.current?.click()}
              >
                choose a file
              </button>{" "}
              to draft the items below.
            </span>
          )}
        </div>

        {pendingParse && (
          <div className="parse-choice">
            <div className="parse-choice-text">
              Parsed <strong>{pendingParse.length}</strong>{" "}
              {pendingParse.length === 1 ? "item" : "items"}. You already have{" "}
              <strong>{items.length}</strong> row
              {items.length === 1 ? "" : "s"} — append the draft or replace what’s
              there?
            </div>
            <div className="parse-choice-actions">
              <button
                type="button"
                className="luna-btn luna-btn-outline luna-btn-sm"
                onClick={appendPending}
              >
                Append
              </button>
              <button
                type="button"
                className="luna-btn luna-btn-outline luna-btn-sm"
                onClick={replacePending}
              >
                Replace
              </button>
              <button
                type="button"
                className="luna-btn luna-btn-ghost luna-btn-sm"
                onClick={() => setPendingParse(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {(hasAiDraft || parseWarnings.length > 0) && (
          <div className="banner warn ai-draft-note">
            <div>
              <strong>AI draft — review before saving.</strong>{" "}
              {parseWarnings.length > 0 ? (
                <>
                  Whole-menu notes from the parser:
                  <ul className="parse-warnings">
                    {parseWarnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </>
              ) : (
                "Drafted rows are flagged below; anything the parser was unsure of carries an inline note on its row."
              )}
            </div>
          </div>
        )}

        <div className="menu-editor">
          {items.length === 0 && (
            <div className="menu-editor-empty">
              No menu items. Drop a menu photo above, or click “Add item” to
              start.
            </div>
          )}
          {items.map((it) => (
            <div key={it.key} className="menu-row-wrap">
              <div
                className={`menu-editor-row${it.aiDraft ? " is-ai" : ""}${
                  it.warning ? " has-warning" : ""
                }`}
              >
                <input
                  className="luna-input"
                  placeholder="name (en)"
                  value={it.nameEn}
                  onChange={(e) =>
                    updateItem(it.key, { nameEn: e.target.value })
                  }
                />
                <input
                  className="luna-input"
                  placeholder="name (es)"
                  value={it.nameEs}
                  onChange={(e) =>
                    updateItem(it.key, { nameEs: e.target.value })
                  }
                />
                <input
                  className="luna-input"
                  placeholder="price"
                  value={it.price}
                  onChange={(e) =>
                    updateItem(it.key, { price: e.target.value })
                  }
                />
                <input
                  className="luna-input"
                  placeholder="category"
                  value={it.category}
                  onChange={(e) =>
                    updateItem(it.key, { category: e.target.value })
                  }
                />
                <input
                  className="luna-input"
                  placeholder="note (optional)"
                  value={it.note}
                  onChange={(e) =>
                    updateItem(it.key, { note: e.target.value })
                  }
                />
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeItem(it.key)}
                  aria-label="Remove menu item"
                >
                  ✕
                </button>
              </div>
              {it.warning && (
                <p className="menu-row-warning" role="note">
                  <span className="menu-row-warning-icon" aria-hidden="true">
                    ⚠
                  </span>
                  {it.warning}
                </p>
              )}
            </div>
          ))}
          <button
            type="button"
            className="luna-btn luna-btn-outline luna-btn-sm"
            onClick={addItem}
            style={{ alignSelf: "flex-start" }}
          >
            + Add item
          </button>
        </div>
      </div>
    </Modal>
  );
}
